import { useEffect, useRef, useState, type FormEvent } from "react";
import { getCopy, type Locale } from "./i18n";
import { localizedPath } from "./route";
import { desktopTrialPolicy } from "./desktopTrialI18n";
import { desktopFixedUSDTPriceCopy, desktopLicenseEmailCopy, desktopLicenseReceiptCopy, desktopPaymentCopy, desktopUSDTPriceCopy } from "./desktopPaymentI18n";
import { desktopOrderPrice, desktopProductPrice, orderStorageKey, paymentRequest, validPaymentURL, type DesktopCheckoutResult, type DesktopPaymentOrder, type DesktopPaymentProduct } from "./desktopPayments";
import { billingCopy, countryOptions } from "./shoplineBilling";

export function DesktopCheckout({ locale, product }: { locale: Locale; product: DesktopPaymentProduct | null }) {
	const copy = desktopPaymentCopy(locale, product?.provider);
  const licenseCopy = desktopLicenseReceiptCopy(locale);
  const priceCopy = desktopUSDTPriceCopy(locale);
  const [email, setEmail] = useState("");
  const [billing, setBilling] = useState({ name: "", country: "", address: "" });
  const cardCopy = billingCopy(locale);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState("");
  const [orderID, setOrderID] = useState("");
  const [order, setOrder] = useState<DesktopPaymentOrder | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    setOrder(null);
    setStatusError(false);
    const id = new URL(window.location.href).searchParams.get("order") || "";
    if (!/^GS-[a-f0-9]{32}$/.test(id)) { setOrderID(""); return; }
    setOrderID(id);
    let token: string | null = null;
    try { token = sessionStorage.getItem(orderStorageKey(id)); } catch { /* handled below */ }
    if (!token) { setStatusError(true); return; }
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const result = await paymentRequest<DesktopPaymentOrder>(`/api/v1/payments/orders/${id}`, { headers: { "X-Order-Token": token! }, signal: abort.signal });
        if (abort.signal.aborted) return;
        setOrder(result);
        setStatusError(false);
        if (["paid", "expired", "failed"].includes(result.status)) return;
      } catch {
        if (abort.signal.aborted) return;
        setStatusError(true);
      }
      timer = setTimeout(poll, 5000);
    }
    void poll();
    return () => { abort.abort(); clearTimeout(timer); };
  }, [locale]);

  async function checkout(event: FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setError("");
    setBusy(true);
    try {
      // The secret stays in this browser tab, out of URLs and analytics.
      try {
        sessionStorage.setItem("grabsilo.checkout.storage", "1");
        sessionStorage.removeItem("grabsilo.checkout.storage");
      } catch { setError(copy.storageError); return; }
	const result = await paymentRequest<DesktopCheckoutResult>("/api/v1/payments/checkout", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, locale, ...(product?.provider === "shopline" ? { billing } : {}) })
      });
	if (!validPaymentURL(result.payment_url, product?.provider, product?.sandbox) || !/^GS-[a-f0-9]{32}$/.test(result.order_id) || !/^[a-f0-9]{64}$/.test(result.order_token)) throw new Error("Invalid checkout response");
      sessionStorage.setItem(orderStorageKey(result.order_id), result.order_token);
      if (product?.provider === "shopline") {
        // SHOPLINE validates the checkout referrer. Share only our origin;
        // order IDs, page paths, and browser tokens never enter that header.
        const policy = document.createElement("meta");
        policy.name = "referrer";
        policy.content = "origin";
        document.head.appendChild(policy);
      }
      window.location.assign(result.payment_url);
    } catch { setError(copy.checkoutError); }
    finally { submitting.current = false; setBusy(false); }
  }

  if (!product?.enabled && !orderID) return null;
  const supportHref = `mailto:support@silosave.com?subject=${encodeURIComponent(`SiloSave Desktop${orderID ? ` - ${orderID}` : ""}`)}`;
  const status = order ? (order.status === "creating" ? copy.waiting : copy[order.status]) : "";
  const displayPrice = product ? desktopProductPrice(product, locale) : "";

  return <div className="desktop-payment-panel" id="desktop-checkout">
    {orderID ? <div className="desktop-payment-receipt" aria-live="polite">
      <h3>{status || copy.orderNumber}</h3>
      <p>{copy.orderNumber}: <strong>{orderID}</strong></p>
      {order && <p>{desktopOrderPrice(order, locale)} · {order.license_label}</p>}
      {order?.sandbox && <p className="desktop-payment-test">{copy.sandbox}</p>}
      {statusError && <p role="alert">{copy.statusError}</p>}
      {order?.license_revoked && <p role="status">{cardCopy.refunded}</p>}
      {order?.status === "paid" && !order.sandbox && !order.license_revoked && <p>{order.license_email_enabled ? desktopLicenseEmailCopy(locale) : order.license_code ? licenseCopy.delivery : copy.delivery}</p>}
      {order?.status === "paid" && !order.sandbox && order.license_code && <div className="desktop-license-receipt">
        <label htmlFor="desktop-issued-license">{licenseCopy.label}</label>
        <textarea id="desktop-issued-license" value={order.license_code} readOnly rows={5} spellCheck={false} onFocus={(event) => event.currentTarget.select()} />
        <button type="button" onClick={async () => {
          setCopyError(false);
          try { await navigator.clipboard.writeText(order.license_code!); setCopied(true); }
          catch { setCopyError(true); }
        }}>{copied ? licenseCopy.copied : licenseCopy.copy}</button>
        {copyError && <p role="status">{licenseCopy.copyError}</p>}
      </div>}
      <a href={supportHref}>{copy.support}</a>
    </div> : <form onSubmit={checkout}>
      <h3>{copy.title}</h3>
      {displayPrice && <p className="desktop-payment-amount">{displayPrice} <span>{product?.license_label}</span></p>}
      {product?.payment_currency === "USDT" && <p>{product.currency === "USDT" ? desktopFixedUSDTPriceCopy(locale) : priceCopy.payment}</p>}
      {product?.provider === "shopline" && <p>{cardCopy.currency}</p>}
      {product?.quote_currency && <p className="desktop-payment-legal"><a href="https://www.exchangerate-api.com" target="_blank" rel="noopener noreferrer">{priceCopy.source}</a></p>}
      {product?.sandbox && <p className="desktop-payment-test">{copy.sandbox}</p>}
      {product?.trial_enabled && product.trial_days === 7 && <p>{desktopTrialPolicy(locale)}</p>}
      <label htmlFor="desktop-payment-email">{copy.email}</label>
      <input id="desktop-payment-email" name="email" type="email" autoComplete="email" maxLength={product?.provider === "shopline" ? 128 : 254} required value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} />
      {product?.provider === "shopline" && <>
        <label htmlFor="desktop-billing-name">{cardCopy.name}</label>
        <input id="desktop-billing-name" name="billing-name" autoComplete="billing name" maxLength={128} required value={billing.name} onChange={(event) => setBilling({ ...billing, name: event.target.value })} disabled={busy} />
        <label htmlFor="desktop-billing-country">{cardCopy.country}</label>
        <select id="desktop-billing-country" name="billing-country" autoComplete="billing country" required value={billing.country} onChange={(event) => setBilling({ ...billing, country: event.target.value })} disabled={busy}>
          <option value="">{cardCopy.choose}</option>
          {countryOptions(locale).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
        <label htmlFor="desktop-billing-address">{cardCopy.address}</label>
        <input id="desktop-billing-address" name="billing-address" autoComplete="billing street-address" maxLength={128} required value={billing.address} onChange={(event) => setBilling({ ...billing, address: event.target.value })} disabled={busy} />
      </>}
      <p>{product?.license_email_enabled ? desktopLicenseEmailCopy(locale) : product?.licensing_enabled ? licenseCopy.delivery : copy.delivery}</p>
      <p className="desktop-payment-legal"><a href={localizedPath(locale, "terms")}>{getCopy(locale).terms}</a> · <a href={localizedPath(locale, "privacy")}>{getCopy(locale).privacy}</a></p>
      {error && <p role="alert">{error}</p>}
      <button type="submit" disabled={busy}>{busy ? copy.busy : copy.pay}</button>
    </form>}
  </div>;
}
